import { expVal } from '@confluence/feature-experiments';

import type { EnhancedSmartLink } from './smartLinkTypes.ts';
import { formatLabel } from './formatLabel.ts';
import { formatProduct } from './formatProduct.ts';

export function formatDaysAgo(dateString: string | undefined, locale: string): string | undefined {
	if (!dateString) {
		return undefined;
	}
	const date = new Date(dateString);
	if (isNaN(date.getTime())) {
		return undefined;
	}
	const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return undefined;
	}
	const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
	if (diffDays < 7) {
		return rtf.format(-diffDays, 'day');
	}
	return rtf.format(-Math.floor(diffDays / 7), 'week');
}

export const getDescription = (
	item: EnhancedSmartLink,
	locale: string,
): (string | null | undefined)[] => {
	// Slot 1 — classification: what kind of thing this is.
	let slot1: string | undefined;
	// Gated by the `1p_3p_connections_byline_twg` experiment. When enabled, slot 1
	// reads "{Product} {resourceType}"; otherwise it keeps the legacy
	// resourceType-preferred behaviour.
	if (expVal('1p_3p_connections_byline_twg', 'isEnabled', false)) {
		// New: "{Product} {resourceType}". Product uses the customer-facing label
		// (never the `townsquare` codename); resourceType is lowercased. Either
		// part is omitted when its source field is absent.
		const productLabel = formatProduct(item.product);
		const resourceTypeLabel = formatLabel(item.resourceType)?.toLowerCase();
		const parts = [productLabel, resourceTypeLabel].filter((part): part is string => Boolean(part));
		// Guard against duplicate words (e.g. townsquare "Goals" app → "Goals goals").
		slot1 =
			parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()
				? parts[0]
				: parts.join(' ') || undefined;
	} else {
		// Legacy: For townsquare/Atlas links the parser sets resourceType to the
		// specific app (Projects/Goals/Focus), so this surfaces the real product
		// even though the group is "Goals".
		slot1 = formatLabel(item.resourceType) || formatLabel(item.product);
	}

	// Slot 2 — person: who created, updated, or owns it.
	const slot2 = item.owner || item.createdBy || item.updatedBy || item.assignedTo;

	// Slot 3 — date: when it was last touched.
	const slot3 =
		formatDaysAgo(item.updatedAt, locale) || formatDaysAgo(item.createdAt, locale) || null;

	return [slot1, slot2, slot3];
};
